import { DrawPlayerLink } from './DrawPlayerLink';
import { DrawStage, DrawMatch, Player, MatchProgression, LadderTemplate } from '@/draw/types';
import { useTranslations, useLocale } from 'next-intl';
import { isCompletedGame } from '@/app/[locale]/match/libs/retirement';
import { useFormatName } from '@/contexts/NameFormattingContext';
import { drawPlayerLabel } from '@/app/[locale]/event/[eventId]/libs/teamLabels';
import { NAME_WRAP } from '@/app/[locale]/event/[eventId]/libs/nameBreaks';
import { NameText } from '@/app/[locale]/event/[eventId]/[slug]/components/NameText';
import { cn } from '@/lib/utils';
import { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { useHydratedTimeZone } from '@/hooks/useHydratedTimeZone';
import { isWinnerlessRow } from '@/draw/core/matchOutcome';
import { useOutcomeLabel } from '@/components/match/matchOutcomeLabel';

// Reconstruct matchProgression from a LadderTemplate definition
// Used when matchProgression isn't persisted (e.g. loaded from DB)
function deriveProgressionsFromTemplate(template: LadderTemplate): MatchProgression[] {
  const progressions: MatchProgression[] = [];
  for (const match of template.matches) {
    for (const [position, slot] of [
      ['player1', match.player1],
      ['player2', match.player2],
    ] as const) {
      if (slot.from !== 'standings' && slot.outcome) {
        progressions.push({
          fromMatchId: `main-${slot.from}`,
          toMatchId: `main-${match.id}`,
          outcome: slot.outcome,
          position,
        });
      }
    }
  }
  return progressions;
}

interface BracketStageVisualizationProps {
  stage: DrawStage;
  playerHref?: (playerId: string) => string | undefined;
  matchProgression?: MatchProgression[];
  onMatchResult?: (matchId: string, winner: Player, loser: Player) => void;
  showExtraInfo?: boolean;
  compactMode?: boolean;
  showTitle?: boolean;
  /** Public embedded mobile view; geometry remains identical at desktop widths. */
  mobileDense?: boolean;
}

interface BracketMatch extends DrawMatch {
  top: number;
  left: number;
  roundIndex: number;
  matchIndex: number;
}

interface BracketRound {
  name: string;
  roundNumber: number;
  matches: BracketMatch[];
  roundIndex: number;
}

// Constants for CSS-based bracket styling - mobile first, then responsive
const BRACKET_CONFIG = {
  matchHeight: 105, // Height of each match card - increased to fit all features
  matchWidth: 320, // Width of each match card - increased to fit 6 score cells
  baseTop: 100, // Top offset for first match
  verticalGap: 20, // Gap between matches in same round
  horizontalGap: 40, // Gap between rounds
  roundHeaderHeight: 40, // Height for round labels
  // Compact mode dimensions
  compactMatchHeight: 80, // Height for compact mode
  compactMatchWidth: 200, // Width for compact mode
  // Height of a one-line player row's content: the 24px score cells, or a text-xs line in compact mode.
  nameRowHeight: 24,
  compactNameRowHeight: 16,
} as const;

function positionTop(top: number, mobileDense: boolean): string {
  return mobileDense ? `calc(${top}px - var(--bracket-mobile-offset, 0px))` : `${top}px`;
}

export function BracketStageVisualization({
  stage,
  playerHref,
  matchProgression = [],
  onMatchResult,
  showExtraInfo = true,
  compactMode = false,
  showTitle = true,
  mobileDense = false,
}: BracketStageVisualizationProps) {
  const t = useTranslations('AdminDraw.drawForm.drawPreview');
  const tMatches = useTranslations('Matches');
  const outcomeLabel = useOutcomeLabel();
  const locale = useLocale();
  const tz = useHydratedTimeZone();
  const formatName = useFormatName();
  const sortedRounds = [...stage.rounds].sort((a, b) => a.roundNumber - b.roundNumber);
  const totalRounds = sortedRounds.length;

  // Collect all match IDs in this stage
  const stageMatchIds = useMemo(() => {
    const ids = new Set<string>();
    for (const round of stage.rounds) {
      for (const m of round.matches) {
        ids.add(m.matchId);
      }
    }
    return ids;
  }, [stage.rounds]);

  // Filter to within-stage progressions for connector lines
  // Fall back to ladderTemplate if no within-stage progressions exist (e.g. Monrad)
  const effectiveProgressions = useMemo(() => {
    const withinStage = matchProgression.filter(
      (p) => stageMatchIds.has(p.fromMatchId) && stageMatchIds.has(p.toMatchId),
    );
    if (withinStage.length > 0) return withinStage;
    if (stage.ladderTemplate) return deriveProgressionsFromTemplate(stage.ladderTemplate);
    return [];
  }, [matchProgression, stageMatchIds, stage.ladderTemplate]);

  // Identify consolation match IDs (all-loser feeders, dead-end)
  const consolationMatchIds = useMemo(() => {
    const ids = new Set<string>();
    if (effectiveProgressions.length === 0) return ids;
    const feederInfo = new Map<string, string[]>(); // matchId → outcomes
    const feedsInto = new Set<string>();
    for (const prog of effectiveProgressions) {
      const existing = feederInfo.get(prog.toMatchId) || [];
      existing.push(prog.outcome);
      feederInfo.set(prog.toMatchId, existing);
      feedsInto.add(prog.fromMatchId);
    }
    for (const [matchId, outcomes] of feederInfo) {
      if (outcomes.every((o) => o === 'loser') && !feedsInto.has(matchId)) {
        ids.add(matchId);
      }
    }
    return ids;
  }, [effectiveProgressions]);

  // State for player highlighting
  const [hoveredPlayerId, setHoveredPlayerId] = useState<string | null>(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // State for drag tracking
  const [isDragging, setIsDragging] = useState(false);
  const mouseInteraction = useRef<{ cleanup: (updateState?: boolean) => void } | null>(null);
  const suppressDragClick = useRef(false);
  const DRAG_THRESHOLD = 5; // pixels

  useEffect(() => () => mouseInteraction.current?.cleanup(false), []);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    mouseInteraction.current?.cleanup();
    suppressDragClick.current = false;
    // Touch and pen gestures stay entirely browser-owned, including on hybrid devices.
    if (event.pointerType !== 'mouse' || event.button !== 0 || !event.isPrimary) return;
    const container = event.currentTarget;
    const pointerId = event.pointerId;
    const startX = event.clientX;
    const startY = event.clientY;
    const initialScrollLeft = container.scrollLeft;
    const initialCursor = container.style.cursor;
    let dragging = false;
    const cleanup = (updateState = true) => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', release);
      document.removeEventListener('pointercancel', cancel);
      window.removeEventListener('blur', blur);
      container.style.cursor = initialCursor;
      mouseInteraction.current = null;
      if (updateState) setIsDragging(false);
    };
    const move = (next: PointerEvent) => {
      if (next.pointerId !== pointerId) return;
      const dx = next.clientX - startX;
      const dy = next.clientY - startY;
      if (!dragging && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) {
        dragging = true;
        setIsDragging(true);
        container.style.cursor = 'grabbing';
      }
      if (dragging) container.scrollLeft = initialScrollLeft - dx * 2;
    };
    const release = (next: PointerEvent) => {
      if (next.pointerId !== pointerId) return;
      suppressDragClick.current = dragging;
      cleanup();
    };
    const cancel = (next: PointerEvent) => {
      if (next.pointerId !== pointerId) return;
      suppressDragClick.current = false;
      cleanup();
    };
    const blur = () => {
      suppressDragClick.current = false;
      cleanup();
    };
    mouseInteraction.current = { cleanup };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', release);
    document.addEventListener('pointercancel', cancel);
    window.addEventListener('blur', blur);
  };

  const handleClickCapture = (event: React.MouseEvent<HTMLDivElement>) => {
    // Keyboard activation has detail=0 and must not inherit a pointer's drag latch.
    if (event.detail === 0 || !suppressDragClick.current) return;
    suppressDragClick.current = false;
    event.preventDefault();
    event.stopPropagation();
  };

  // Helper function to find where a player comes from. Searches the full
  // matchProgression so cross-stage feeders (e.g. qualifying pool → main draw)
  // are surfaced as labels, not just within-stage progressions used for connectors.
  const findPlayerSource = (matchId: string, position: 'player1' | 'player2') => {
    return matchProgression.find(
      (progression) => progression.toMatchId === matchId && progression.position === position,
    );
  };

  // Planned seat (qualifying pools → tier): "Pool 1 · #3" before the pool finishes
  const seatPlan =
    (
      stage.settings as
        | {
            seatPlan?: Array<{
              pool: number;
              rank: number;
              matchIndex: number;
              position: 'player1' | 'player2';
            }>;
          }
        | undefined
    )?.seatPlan ?? [];
  const firstRoundMatchIds = sortedRounds[0]?.matches.map((m) => m.matchId) ?? [];
  const findPlannedSeat = (matchId: string, position: 'player1' | 'player2') => {
    const matchIndex = firstRoundMatchIds.indexOf(matchId);
    if (matchIndex < 0) return undefined;
    return seatPlan.find((e) => e.matchIndex === matchIndex && e.position === position);
  };

  // Helper function to get all match IDs containing a specific player
  const getPlayerMatches = (playerId: string): string[] => {
    const matchIds: string[] = [];

    stage.rounds.forEach((round) => {
      round.matches.forEach((match) => {
        if (
          (match.player1?.id === playerId && !match.player1?.isBye) ||
          (match.player2?.id === playerId && !match.player2?.isBye)
        ) {
          matchIds.push(match.matchId);
        }
      });
    });

    return matchIds;
  };

  // Calculate CSS-based bracket layout with absolute positioning
  // Supports both standard power-of-2 brackets AND asymmetric ladders (e.g., futsal show)
  const calculateLayout = (): BracketRound[] => {
    // Use compact dimensions if in compact mode
    const mHeight = matchHeight;
    const mWidth = compactMode ? BRACKET_CONFIG.compactMatchWidth : BRACKET_CONFIG.matchWidth;

    // Check if this is a standard power-of-2 bracket
    const isStandardBracket = sortedRounds.every((round, idx) => {
      const expected = Math.pow(2, totalRounds - 1 - idx);
      return round.matches.length === expected;
    });

    if (isStandardBracket || effectiveProgressions.length === 0) {
      // Standard power-of-2 layout (original algorithm)
      const rounds: BracketRound[] = [];

      for (let roundNum = 0; roundNum < totalRounds; roundNum++) {
        const round: BracketRound = {
          name: sortedRounds[roundNum].name,
          roundNumber: sortedRounds[roundNum].roundNumber,
          matches: [],
          roundIndex: roundNum,
        };

        const verticalSpacing = (mHeight + BRACKET_CONFIG.verticalGap) * Math.pow(2, roundNum);
        const left = roundNum * (mWidth + BRACKET_CONFIG.horizontalGap);

        // Render every match in this round. For real power-of-2 brackets this equals
        // 2^(rounds-1-roundNum); for a flat placement stage (one round, N independent
        // placement matches) it renders all N as a column instead of only the first.
        const matchesInRound = sortedRounds[roundNum].matches.length;
        for (let matchNum = 0; matchNum < matchesInRound; matchNum++) {
          const originalMatch = sortedRounds[roundNum].matches[matchNum];
          if (!originalMatch) continue;

          let top = BRACKET_CONFIG.baseTop + matchNum * verticalSpacing;
          if (roundNum > 0) {
            const offset = (verticalSpacing - mHeight) / 2 - BRACKET_CONFIG.verticalGap;
            top += offset;
          }

          round.matches.push({
            ...originalMatch,
            top,
            left,
            roundIndex: roundNum,
            matchIndex: matchNum,
          });
        }

        rounds.push(round);
      }
      return rounds;
    }

    // --- Asymmetric bracket layout using effectiveProgressions ---
    // Build feeder map: matchId → list of source matchIds
    const feederMap = new Map<string, string[]>();
    for (const prog of effectiveProgressions) {
      const existing = feederMap.get(prog.toMatchId) || [];
      existing.push(prog.fromMatchId);
      feederMap.set(prog.toMatchId, existing);
    }

    // Count main-bracket matches per round (excluding consolation)
    const mainMatchCounts = sortedRounds.map(
      (r) => r.matches.filter((m) => !consolationMatchIds.has(m.matchId)).length,
    );
    const maxMainMatchesInAnyRound = Math.max(...mainMatchCounts, 1);
    const baseSpacing = mHeight + BRACKET_CONFIG.verticalGap;

    // First pass: position main bracket matches only
    const positionMap = new Map<string, number>(); // matchId → top position
    const rounds: BracketRound[] = [];

    for (let roundNum = 0; roundNum < totalRounds; roundNum++) {
      const roundMatches = sortedRounds[roundNum].matches;
      const mainMatches = roundMatches.filter((m) => !consolationMatchIds.has(m.matchId));
      const left = roundNum * (mWidth + BRACKET_CONFIG.horizontalGap);
      const round: BracketRound = {
        name: sortedRounds[roundNum].name,
        roundNumber: sortedRounds[roundNum].roundNumber,
        matches: [],
        roundIndex: roundNum,
      };

      // Position main matches
      for (let matchNum = 0; matchNum < mainMatches.length; matchNum++) {
        const originalMatch = mainMatches[matchNum];
        const feeders = feederMap.get(originalMatch.matchId) || [];
        const resolvedFeeders = feeders.filter((fId) => positionMap.has(fId));

        let top: number;
        if (resolvedFeeders.length >= 2) {
          const feederTops = resolvedFeeders.map((fId) => positionMap.get(fId)! + mHeight / 2);
          const avgCenter = feederTops.reduce((a, b) => a + b, 0) / feederTops.length;
          top = avgCenter - mHeight / 2;
        } else if (resolvedFeeders.length === 1) {
          top = positionMap.get(resolvedFeeders[0])!;
        } else {
          const totalRoundHeight = mainMatches.length * baseSpacing - BRACKET_CONFIG.verticalGap;
          const maxTotalHeight =
            maxMainMatchesInAnyRound * baseSpacing - BRACKET_CONFIG.verticalGap;
          const roundOffset = (maxTotalHeight - totalRoundHeight) / 2;
          top = BRACKET_CONFIG.baseTop + roundOffset + matchNum * baseSpacing;
        }

        positionMap.set(originalMatch.matchId, top);
        round.matches.push({
          ...originalMatch,
          top,
          left,
          roundIndex: roundNum,
          matchIndex: matchNum,
        });
      }

      // Resolve collisions within main matches
      round.matches.sort((a, b) => a.top - b.top);
      for (let i = 1; i < round.matches.length; i++) {
        const prev = round.matches[i - 1];
        const curr = round.matches[i];
        const minTop = prev.top + mHeight + BRACKET_CONFIG.verticalGap;
        if (curr.top < minTop) {
          curr.top = minTop;
          positionMap.set(curr.matchId, curr.top);
        }
      }

      rounds.push(round);
    }

    // Second pass: position consolation matches below the main bracket
    const allMainTops = [...positionMap.values()];
    const mainBracketBottom =
      allMainTops.length > 0 ? Math.max(...allMainTops) + mHeight : BRACKET_CONFIG.baseTop;
    const consolationTopStart = mainBracketBottom + BRACKET_CONFIG.verticalGap * 3; // Extra gap

    for (let roundNum = 0; roundNum < totalRounds; roundNum++) {
      const consolationMatches = sortedRounds[roundNum].matches.filter((m) =>
        consolationMatchIds.has(m.matchId),
      );
      if (consolationMatches.length === 0) continue;

      const left = roundNum * (mWidth + BRACKET_CONFIG.horizontalGap);
      const round = rounds[roundNum];

      for (let matchNum = 0; matchNum < consolationMatches.length; matchNum++) {
        const originalMatch = consolationMatches[matchNum];
        const top = consolationTopStart + matchNum * baseSpacing;

        positionMap.set(originalMatch.matchId, top);
        round.matches.push({
          ...originalMatch,
          top,
          left,
          roundIndex: roundNum,
          matchIndex: round.matches.length,
        });
      }
    }

    return rounds;
  };

  // Names are shown in full and wrap at their spaces, so a long name makes its row taller. Every
  // card grows by the tallest card's extra name lines: cards stay one height, so the layout and
  // the connectors (both drawn from card centres) stay exact. Measured after render, and again
  // when a name's box resizes (web fonts loading).
  const boardRef = useRef<HTMLDivElement>(null);
  const [nameExtra, setNameExtra] = useState(0);
  useLayoutEffect(() => {
    const board = boardRef.current;
    if (!board) return;
    const rowHeight = compactMode
      ? BRACKET_CONFIG.compactNameRowHeight
      : BRACKET_CONFIG.nameRowHeight;
    const measure = () => {
      let extra = 0;
      board.querySelectorAll<HTMLElement>('[data-match-card]').forEach((card) => {
        let cardExtra = 0;
        card.querySelectorAll<HTMLElement>('[data-player-name]').forEach((name) => {
          cardExtra += Math.max(0, name.offsetHeight - rowHeight);
        });
        extra = Math.max(extra, cardExtra);
      });
      setNameExtra(extra);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    board.querySelectorAll('[data-player-name]').forEach((name) => observer.observe(name));
    return () => observer.disconnect();
  }, [stage, compactMode, formatName]);
  const matchHeight =
    (compactMode ? BRACKET_CONFIG.compactMatchHeight : BRACKET_CONFIG.matchHeight) + nameExtra;

  const rounds = calculateLayout();

  // Detect touch devices to disable hover behavior
  useEffect(() => {
    const checkTouchDevice = () => {
      setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    };

    checkTouchDevice();
    window.addEventListener('resize', checkTouchDevice);

    return () => window.removeEventListener('resize', checkTouchDevice);
  }, []);

  // Helper function to determine if a match should be highlighted
  const isMatchHighlighted = (matchId: string): boolean => {
    const activePlayerId = selectedPlayerId || hoveredPlayerId;
    if (!activePlayerId) return false;

    const playerMatches = getPlayerMatches(activePlayerId);
    return playerMatches.includes(matchId);
  };

  // Helper function to determine if a player should be highlighted
  const isPlayerHighlighted = (playerId: string): boolean => {
    const activePlayerId = selectedPlayerId || hoveredPlayerId;
    return activePlayerId === playerId;
  };

  // Event handlers for player highlighting
  const handlePlayerHover = (playerId: string | null) => {
    setHoveredPlayerId(playerId);
  };

  const handlePlayerClick = (playerId: string) => {
    // Toggle selection - if same player clicked, deselect; otherwise select new player
    if (selectedPlayerId === playerId) {
      setSelectedPlayerId(null);
    } else {
      setSelectedPlayerId(playerId);
    }
  };

  const handleContainerClick = (e: React.MouseEvent) => {
    // Only clear selection if clicking on empty space (not on match cards or players)
    if ((e.target as HTMLElement).closest('[data-match-card]')) {
      return;
    }
    setSelectedPlayerId(null);
  };

  // Handle player click to simulate match result (admin functionality)
  const handlePlayerMatchResult = (match: BracketMatch, clickedPlayer: Player) => {
    if (
      !onMatchResult ||
      match.score ||
      match.winner ||
      match.isBye ||
      isWinnerlessRow({ outcome: match.outcome, is_walkover: match.isWalkover })
    ) {
      return; // Don't allow clicks if match is already resolved (incl. winner-less), is a {t('bye')}, or no callback provided
    }

    const otherPlayer = clickedPlayer.id === match.player1?.id ? match.player2 : match.player1;
    if (!otherPlayer) {
      return; // Need both players to simulate a match
    }

    // Simulate 3-0 win
    const winner = clickedPlayer;
    const loser = otherPlayer;

    onMatchResult(match.matchId, winner, loser);
  };

  // Calculate container dimensions for CSS layout
  const matchWidth = compactMode ? BRACKET_CONFIG.compactMatchWidth : BRACKET_CONFIG.matchWidth;

  const totalWidth = totalRounds * (matchWidth + BRACKET_CONFIG.horizontalGap) + 100;

  // Calculate height from actual match positions
  const allMatchPositions = rounds.flatMap((r) => r.matches.map((m) => m.top));
  const maxBottom =
    allMatchPositions.length > 0
      ? Math.max(...allMatchPositions) + matchHeight
      : BRACKET_CONFIG.baseTop + matchHeight;
  const totalHeight = maxBottom + 60;

  // Render individual match card as CSS div
  const renderMatchCard = (match: BracketMatch) => {
    const player1Source = findPlayerSource(match.matchId, 'player1');
    const player2Source = findPlayerSource(match.matchId, 'player2');
    const player1Seat = findPlannedSeat(match.matchId, 'player1');
    const player2Seat = findPlannedSeat(match.matchId, 'player2');

    const winnerless = isWinnerlessRow({ outcome: match.outcome, is_walkover: match.isWalkover });
    const isSingleSidedWalkover =
      match.outcome === 'walkover' || (match.outcome == null && !!match.isWalkover);
    // A completed dead branch has no future winner/loser to populate its empty seat.
    const resolvedEmptyWalkover =
      match.status === 'completed' &&
      isSingleSidedWalkover &&
      !match.isBye &&
      !!match.player1 !== !!match.player2;
    const isPlayer1Clickable =
      !match.score &&
      !match.winner &&
      !match.isBye &&
      !winnerless &&
      onMatchResult &&
      !match.player1?.isBye;
    const isPlayer2Clickable =
      !match.score &&
      !match.winner &&
      !match.isBye &&
      !winnerless &&
      onMatchResult &&
      !match.player2?.isBye;

    // Per-player amber walkover chip: single-sided walkover shows it on the
    // loser only; dual_withdrawal is symmetric — both players. not_played gets
    // no chip at all (grey label lives in the score grid instead, below).
    const showWalkoverChip = (playerId: string | undefined): boolean => {
      if (!playerId) return false;
      if (match.outcome === 'dual_withdrawal') return true;
      if (match.outcome === 'not_played') return false;
      return isSingleSidedWalkover && match.loser?.id === playerId;
    };

    const isHighlighted = isMatchHighlighted(match.matchId);

    const courtName = match.eventMatchSchedule?.court_name?.trim();
    const matchLabel = courtName ? `${courtName} · ${match.matchId}` : match.matchId;

    return (
      <div
        key={match.matchId}
        data-match-card
        data-match-id={match.matchId}
        className={`border-border bg-background absolute rounded-lg border shadow-sm transition-all duration-150 ease-in-out ${
          isHighlighted ? 'bg-accent/10 ring-accent ring-2' : ''
        }`}
        style={{
          top: positionTop(match.top, mobileDense),
          left: `${match.left}px`,
          width: `${matchWidth}px`,
          height: `${matchHeight}px`,
        }}
      >
        {/* Player 1 */}
        <div
          className={`border-border text-foreground flex items-center justify-between gap-2 border-b ${
            compactMode ? 'px-2 py-1' : 'px-3 py-2'
          } transition-all duration-150 ease-in-out ${
            isPlayer1Clickable
              ? 'cursor-pointer hover:bg-orange-100 dark:hover:bg-orange-900/30'
              : match.player1 && !match.player1.isBye
                ? 'cursor-pointer'
                : ''
          } ${
            match.player1 && !match.player1.isBye && isPlayerHighlighted(match.player1.id)
              ? 'bg-accent/20 ring-accent ring-1'
              : ''
          }`}
          onClick={
            isDragging
              ? undefined
              : isPlayer1Clickable
                ? () => handlePlayerMatchResult(match, match.player1!)
                : match.player1 && !match.player1.isBye
                  ? () => handlePlayerClick(match.player1!.id)
                  : undefined
          }
          onMouseEnter={
            !isTouchDevice && match.player1 && !match.player1.isBye
              ? () => handlePlayerHover(match.player1!.id)
              : undefined
          }
          onMouseLeave={
            !isTouchDevice && match.player1 && !match.player1.isBye
              ? () => handlePlayerHover(null)
              : undefined
          }
        >
          <div className="flex min-w-0 flex-1 items-center space-x-2">
            {match.player1?.seed && !compactMode && (
              <span className="mr-1 shrink-0 rounded bg-blue-100 px-1.5 py-0.5 text-xs font-bold text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
                #{match.player1.seed}
              </span>
            )}
            <span
              data-player-name
              className={cn('min-w-0 font-medium', NAME_WRAP, compactMode ? 'text-xs' : 'text-sm')}
            >
              {match.player1 ? (
                match.player1.isBye ? (
                  <span className="text-muted text-xs font-semibold">BYE</span>
                ) : (
                  <>
                    {match.player1.seed && compactMode && (
                      <span className="mr-1">[{match.player1.seed}]</span>
                    )}
                    <DrawPlayerLink playerId={match.player1.id} playerHref={playerHref}>
                      <NameText name={drawPlayerLabel(match.player1, formatName)} />
                    </DrawPlayerLink>
                    {showWalkoverChip(match.player1.id) && (
                      <span className="ml-1.5 rounded bg-amber-100 px-1 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                        {tMatches('walkoverShort')}
                      </span>
                    )}
                  </>
                )
              ) : resolvedEmptyWalkover ? (
                <span className="text-warning-emphasis text-xs font-semibold">
                  {tMatches('walkoverShort')}
                </span>
              ) : player1Source ? (
                <span className="text-xs opacity-60">
                  {t(player1Source.outcome === 'winner' ? 'winnerOf' : 'loserOf', {
                    matchId: player1Source.fromMatchId,
                  })}
                </span>
              ) : player1Seat ? (
                <span className="text-muted text-xs">
                  {t('poolPosition', { pool: player1Seat.pool, rank: player1Seat.rank + 1 })}
                </span>
              ) : (
                <span className="text-muted text-xs">{t('tbd')}</span>
              )}
            </span>
          </div>
          {/* Game scores for player 1 - Only show in non-compact mode */}
          {!compactMode &&
            (match.outcome === 'not_played' ? (
              <div className="text-muted flex h-6 shrink-0 items-center px-1 text-xs font-medium">
                {outcomeLabel('not_played', false)}
              </div>
            ) : (
              <div className="flex shrink-0 space-x-1">
                {Array.from({ length: 6 }, (_, index) => {
                  let displayValue = '-';
                  if (index === 0) {
                    // Live points can include an unfinished game; use canonical totals.
                    if (match.gamesWon) {
                      displayValue = match.gamesWon.player1.toString();
                    } else if (
                      match.gamesWon === undefined &&
                      match.score &&
                      match.score.length > 0
                    ) {
                      const gamesWon = match.score.filter(
                        (game) => isCompletedGame(game) && game.player1Score > game.player2Score,
                      ).length;
                      displayValue = gamesWon.toString();
                    }
                  } else {
                    // Remaining cells: show individual game points
                    const gameScore =
                      match.score && match.score[index - 1]
                        ? match.score[index - 1].player1Score
                        : '-';
                    displayValue = gameScore.toString();
                  }

                  const isEven = index % 2 === 0;
                  return (
                    <div
                      key={index}
                      className={`flex h-6 w-6 items-center justify-center border font-mono text-xs font-bold ${
                        isEven
                          ? 'border-gray-300 bg-white text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
                          : 'border-gray-300 bg-gray-200 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100'
                      }`}
                    >
                      {displayValue}
                    </div>
                  );
                })}
                {match.outcome === 'retired' && match.winner?.id === match.player1?.id && (
                  <span className="text-muted flex items-center text-xs">
                    {tMatches('retiredShort')}
                  </span>
                )}
              </div>
            ))}
        </div>

        {/* Player 2 */}
        <div
          className={`text-foreground flex items-center justify-between gap-2 ${
            compactMode ? 'px-2 py-1' : 'px-3 py-2'
          } transition-all duration-150 ease-in-out ${
            isPlayer2Clickable
              ? 'cursor-pointer hover:bg-orange-100 dark:hover:bg-orange-900/30'
              : match.player2 && !match.player2.isBye
                ? 'cursor-pointer'
                : ''
          } ${
            match.player2 && !match.player2.isBye && isPlayerHighlighted(match.player2.id)
              ? 'bg-accent/20 ring-accent ring-1'
              : ''
          }`}
          onClick={
            isDragging
              ? undefined
              : isPlayer2Clickable
                ? () => handlePlayerMatchResult(match, match.player2!)
                : match.player2 && !match.player2.isBye
                  ? () => handlePlayerClick(match.player2!.id)
                  : undefined
          }
          onMouseEnter={
            !isTouchDevice && match.player2 && !match.player2.isBye
              ? () => handlePlayerHover(match.player2!.id)
              : undefined
          }
          onMouseLeave={
            !isTouchDevice && match.player2 && !match.player2.isBye
              ? () => handlePlayerHover(null)
              : undefined
          }
        >
          <div className="flex min-w-0 flex-1 items-center space-x-2">
            {match.player2?.seed && !compactMode && (
              <span className="mr-1 shrink-0 rounded bg-blue-100 px-1.5 py-0.5 text-xs font-bold text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
                #{match.player2.seed}
              </span>
            )}
            <span
              data-player-name
              className={cn('min-w-0 font-medium', NAME_WRAP, compactMode ? 'text-xs' : 'text-sm')}
            >
              {match.player2 ? (
                match.player2.isBye ? (
                  <span className="text-muted text-xs font-semibold">BYE</span>
                ) : (
                  <>
                    {match.player2.seed && compactMode && (
                      <span className="mr-1">[{match.player2.seed}]</span>
                    )}
                    <DrawPlayerLink playerId={match.player2.id} playerHref={playerHref}>
                      <NameText name={drawPlayerLabel(match.player2, formatName)} />
                    </DrawPlayerLink>
                    {showWalkoverChip(match.player2.id) && (
                      <span className="ml-1.5 rounded bg-amber-100 px-1 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                        {tMatches('walkoverShort')}
                      </span>
                    )}
                  </>
                )
              ) : resolvedEmptyWalkover ? (
                <span className="text-warning-emphasis text-xs font-semibold">
                  {tMatches('walkoverShort')}
                </span>
              ) : player2Source ? (
                <span className="text-xs opacity-60">
                  {t(player2Source.outcome === 'winner' ? 'winnerOf' : 'loserOf', {
                    matchId: player2Source.fromMatchId,
                  })}
                </span>
              ) : player2Seat ? (
                <span className="text-muted text-xs">
                  {t('poolPosition', { pool: player2Seat.pool, rank: player2Seat.rank + 1 })}
                </span>
              ) : (
                <span className="text-muted text-xs">{t('tbd')}</span>
              )}
            </span>
          </div>
          {/* Game scores for player 2 - Only show in non-compact mode */}
          {!compactMode &&
            (match.outcome === 'not_played' ? (
              <div className="text-muted flex h-6 shrink-0 items-center px-1 text-xs font-medium">
                {outcomeLabel('not_played', false)}
              </div>
            ) : (
              <div className="flex shrink-0 space-x-1">
                {Array.from({ length: 6 }, (_, index) => {
                  let displayValue = '-';
                  if (index === 0) {
                    // Live points can include an unfinished game; use canonical totals.
                    if (match.gamesWon) {
                      displayValue = match.gamesWon.player2.toString();
                    } else if (
                      match.gamesWon === undefined &&
                      match.score &&
                      match.score.length > 0
                    ) {
                      const gamesWon = match.score.filter(
                        (game) => isCompletedGame(game) && game.player2Score > game.player1Score,
                      ).length;
                      displayValue = gamesWon.toString();
                    }
                  } else {
                    // Remaining cells: show individual game points
                    const gameScore =
                      match.score && match.score[index - 1]
                        ? match.score[index - 1].player2Score
                        : '-';
                    displayValue = gameScore.toString();
                  }

                  const isEven = index % 2 === 0;
                  return (
                    <div
                      key={index}
                      className={`flex h-6 w-6 items-center justify-center border font-mono text-xs font-bold ${
                        isEven
                          ? 'border-gray-300 bg-white text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
                          : 'border-gray-300 bg-gray-200 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100'
                      }`}
                    >
                      {displayValue}
                    </div>
                  );
                })}
                {match.outcome === 'retired' && match.winner?.id === match.player2?.id && (
                  <span className="text-muted flex items-center text-xs">
                    {tMatches('retiredShort')}
                  </span>
                )}
              </div>
            ))}
        </div>

        {/* Match Info - Show match ID and relevant info */}
        <div
          className={`text-muted border-border border-t text-xs ${
            compactMode ? 'px-1 pt-0.5 pb-1' : 'px-2 pt-1 pb-2'
          }`}
        >
          {compactMode ? (
            /* Compact mode: Only show court + match ID */
            <div className="truncate text-center font-mono text-xs font-semibold">{matchLabel}</div>
          ) : (
            /* Normal mode: Show date/time and match ID */
            <div className="flex items-center justify-between">
              {/* Left-aligned date/time */}
              <div className="truncate">
                {match.isBye ? (
                  <div className="text-muted-foreground truncate text-xs">—</div>
                ) : match.eventMatchSchedule?.scheduled_datetime ? (
                  <div className="truncate font-medium">
                    {new Date(match.eventMatchSchedule.scheduled_datetime).toLocaleDateString(
                      locale,
                      {
                        month: 'short',
                        day: 'numeric',
                        timeZone: tz,
                      },
                    )}{' '}
                    {new Date(match.eventMatchSchedule.scheduled_datetime).toLocaleTimeString(
                      locale,
                      {
                        hour: '2-digit',
                        minute: '2-digit',
                        timeZone: tz,
                      },
                    )}
                  </div>
                ) : (
                  <div className="truncate text-xs">{t('tbd')}</div>
                )}
              </div>

              {/* Right-aligned court + match ID */}
              <div className="truncate font-mono text-xs font-semibold">{matchLabel}</div>
            </div>
          )}
        </div>

        {/* Bracket connector exit stub - only for fallback (no progressions) */}
        {effectiveProgressions.length === 0 && match.roundIndex < totalRounds - 1 && (
          <div
            className="bg-border absolute top-1/2 h-px"
            style={{
              transform: 'translateY(-50%)',
              left: `${matchWidth}px`,
              width: `${BRACKET_CONFIG.horizontalGap / 2}px`,
            }}
          />
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {showTitle && <h3 className="mt-4 text-lg font-semibold">{stage.name}</h3>}
      {showExtraInfo && (
        <p className="text-muted-foreground text-sm">
          {totalRounds} {t('rounds')}
        </p>
      )}

      <div
        className="cursor-grab overflow-x-auto px-2 active:cursor-grabbing"
        style={{
          touchAction: 'auto',
          scrollbarWidth: 'thin',
          scrollbarColor: 'hsl(var(--border)) transparent',
        }}
        onClick={handleContainerClick}
        onPointerDown={handlePointerDown}
        onClickCapture={handleClickCapture}
      >
        <div
          ref={boardRef}
          className={`relative select-none ${mobileDense ? '[--bracket-mobile-offset:clamp(0px,calc(100px_-_3rem),52px)] md:[--bracket-mobile-offset:0px]' : ''}`}
          style={{
            width: `${totalWidth}px`,
            height: positionTop(totalHeight, mobileDense),
            minWidth: '100%',
          }}
        >
          {/* Round labels */}
          {rounds.map((round, roundIndex) => {
            const labelLeft =
              roundIndex * (matchWidth + BRACKET_CONFIG.horizontalGap) + matchWidth / 2;

            return (
              <div
                key={`label-${roundIndex}`}
                className="text-foreground absolute text-center font-bold"
                style={{
                  top: '20px',
                  left: `${labelLeft}px`,
                  transform: 'translateX(-50%)',
                }}
              >
                {round.name}
              </div>
            );
          })}

          {/* Match cards */}
          {rounds.map((round) => round.matches.map((match) => renderMatchCard(match)))}

          {/* Consolation section label */}
          {consolationMatchIds.size > 0 &&
            (() => {
              const consolationMatches = rounds.flatMap((r) =>
                r.matches.filter((m) => consolationMatchIds.has(m.matchId)),
              );
              if (consolationMatches.length === 0) return null;
              const minTop = Math.min(...consolationMatches.map((m) => m.top));
              return (
                <div
                  className="text-muted-foreground absolute text-sm font-semibold italic"
                  style={{
                    top: positionTop(minTop - 28, mobileDense),
                    left: `${consolationMatches[0].left}px`,
                  }}
                >
                  Consolation
                </div>
              );
            })()}

          {/* Connecting lines between rounds — uses matchProgression data when available */}
          {(() => {
            // Build a lookup: matchId → BracketMatch for quick position access
            const matchMap = new Map<string, BracketMatch>();
            rounds.forEach((round) => round.matches.forEach((m) => matchMap.set(m.matchId, m)));

            if (effectiveProgressions.length > 0) {
              // Use actual progression data to draw correct connectors
              // Skip consolation matches — they're visually separate (like a plate draw)
              const targetGroups = new Map<
                string,
                { sources: BracketMatch[]; target: BracketMatch }
              >();

              for (const prog of effectiveProgressions) {
                // Skip progressions to/from consolation matches
                if (consolationMatchIds.has(prog.toMatchId)) continue;
                const source = matchMap.get(prog.fromMatchId);
                const target = matchMap.get(prog.toMatchId);
                if (!source || !target) continue;

                const existing = targetGroups.get(prog.toMatchId);
                if (existing) {
                  existing.sources.push(source);
                } else {
                  targetGroups.set(prog.toMatchId, {
                    sources: [source],
                    target,
                  });
                }
              }

              const connectors: React.ReactNode[] = [];

              targetGroups.forEach(({ sources, target }, targetId) => {
                const targetCenter = target.top + matchHeight / 2;
                const mergerX = target.left - BRACKET_CONFIG.horizontalGap / 2;

                const allCenters = sources.map((s) => s.top + matchHeight / 2);
                allCenters.push(targetCenter);
                const minCenter = Math.min(...allCenters);
                const maxCenter = Math.max(...allCenters);

                // Horizontal lines from each source to merger
                sources.forEach((source) => {
                  const sourceCenter = source.top + matchHeight / 2;
                  const sourceRight = source.left + matchWidth;

                  connectors.push(
                    <div
                      key={`horiz-${source.matchId}-${targetId}`}
                      className="bg-border absolute h-px"
                      style={{
                        top: positionTop(sourceCenter, mobileDense),
                        left: `${sourceRight}px`,
                        width: `${mergerX - sourceRight}px`,
                        transform: 'translateY(-50%)',
                      }}
                    />,
                  );
                });

                // Vertical merger line
                const vertHeight = maxCenter - minCenter;
                if (vertHeight > 0) {
                  connectors.push(
                    <div
                      key={`vert-${targetId}`}
                      className="bg-border absolute"
                      style={{
                        top: positionTop(minCenter, mobileDense),
                        left: `${mergerX}px`,
                        width: '1px',
                        height: `${vertHeight}px`,
                      }}
                    />,
                  );
                }

                // Entry line into target
                connectors.push(
                  <div
                    key={`entry-${targetId}`}
                    className="bg-border absolute h-px"
                    style={{
                      top: positionTop(targetCenter, mobileDense),
                      left: `${mergerX}px`,
                      width: `${BRACKET_CONFIG.horizontalGap / 2}px`,
                      transform: 'translateY(-50%)',
                    }}
                  />,
                );
              });

              return connectors;
            }

            // Fallback: power-of-2 index-based connectors for standard brackets
            return rounds.flatMap((round, roundIndex) => {
              if (roundIndex >= totalRounds - 1) return [];

              return round.matches.flatMap((match, matchIndex) => {
                const nextRound = rounds[roundIndex + 1];
                const nextMatchIndex = Math.floor(matchIndex / 2);
                const nextMatch = nextRound?.matches[nextMatchIndex];
                if (!nextMatch) return [];

                const currentMatchCenter = match.top + matchHeight / 2;
                const nextMatchCenter = nextMatch.top + matchHeight / 2;
                const lineTop = Math.min(currentMatchCenter, nextMatchCenter);
                const lineHeight = Math.abs(currentMatchCenter - nextMatchCenter);
                const verticalLineLeft = match.left + matchWidth + BRACKET_CONFIG.horizontalGap / 2;

                return [
                  <div
                    key={`connector-${match.matchId}`}
                    className="bg-border absolute"
                    style={{
                      top: positionTop(lineTop, mobileDense),
                      left: `${verticalLineLeft}px`,
                      width: '1px',
                      height: `${lineHeight}px`,
                    }}
                  />,
                  <div
                    key={`entry-${match.matchId}`}
                    className="bg-border absolute h-px"
                    style={{
                      top: positionTop(nextMatchCenter, mobileDense),
                      left: `${verticalLineLeft}px`,
                      width: `${BRACKET_CONFIG.horizontalGap / 2}px`,
                      transform: 'translateY(-50%)',
                    }}
                  />,
                ];
              });
            });
          })()}
        </div>
      </div>
    </div>
  );
}
